import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card } from '../components';
import { authService } from '../services';
import '../styles/Profile.scss';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    designation: '',
    company_name: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setFormData({
          full_name: `${userData.first_name} ${userData.last_name}`.trim(),
          email: userData.email,
          designation: userData.designation || '',
          company_name: userData.company_name || '',
        });
      } catch (err) {
        console.error('Failed to load user:', err);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
    // Clear success message on edit
    setSuccessMessage('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    }
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    } else if (formData.company_name.length > 100) {
      newErrors.company_name = 'Company name cannot exceed 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setSuccessMessage('');

    try {
      // Split full name into first and last name
      const nameParts = formData.full_name.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || '';

      // Prepare update data
      const updateData = {
        first_name: firstName,
        last_name: lastName,
        email: formData.email,
        designation: formData.designation,
        company_name: formData.company_name,  
      };

      // Call update endpoint
      const updatedUser = await authService.updateProfile(updateData);
      setUser(updatedUser);
      setSuccessMessage('Profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrors({
        general: error.response?.data?.detail || 'Failed to update profile. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <Card title="Edit Profile" className="profile-card">
        {successMessage && <div className="success-banner">{successMessage}</div>}
        {errors.general && <div className="error-banner">{errors.general}</div>}

        <form onSubmit={handleSubmit}>
          <Input
            label="Full Name"
            type="text"
            name="full_name"
            placeholder="Enter your full name"
            value={formData.full_name}
            onChange={handleChange}
            error={errors.full_name}
            required
          />

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            required
          />

          <Input
            label="Designation"
            type="text"
            name="designation"
            placeholder="Enter your job title or designation"
            value={formData.designation}
            onChange={handleChange}
            error={errors.designation}
            required
          />

          <Input
            label="Company Name"
            type="text"
            name="company_name"
            placeholder="Enter your company name"
            value={formData.company_name}
            onChange={handleChange}
            error={errors.company_name}
            required
          />

          <div className="profile-actions">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/')}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </div>
        </form>

        {user && (
          <div className="user-info">
            <h3>Current Information</h3>
            <p>
              <strong>Username:</strong> {user.username}
            </p>
            <p>
              <strong>Account Created:</strong> {new Date(user.created_at).toLocaleDateString()}
            </p>
            <p>
              <strong>Status:</strong> {user.is_active ? 'Active' : 'Inactive'}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Profile;
